"use client";

import React, { useState, useEffect } from "react";
import { Spinner, Container, Alert } from "react-bootstrap";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { BlogPost } from "@/app/types/blog";
import { useToast } from "@/app/hooks/use-toast";
import NavbarSection from "@/app/components/NavbarSection";
import FooterSection from "@/app/components/FooterSection";
import BlogDetailContent from "./BlogDetailContent";
import { fetchBlogPost } from "@/app/services/blogApi";

interface Props {
  slug: string;
  initialPost: BlogPost | null;
}

const BlogDetailClient: React.FC<Props> = ({ slug, initialPost }) => {
  const [post, setPost] = useState<BlogPost | null>(initialPost);
  const [loading, setLoading] = useState(!initialPost);
  const { toast } = useToast();

  useEffect(() => {
    if (initialPost) {
      setPost(initialPost);
      setLoading(false);
      return;
    }

    const loadPost = async () => {
      try {
        setLoading(true);
        const result = await fetchBlogPost(slug);
        
        // If redirect URL is present, redirect the user (client-side)
        if (result.redirectUrl) {
          window.location.href = result.redirectUrl;
          return;
        }
        
        setPost(result.post ?? null);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load blog post.",
          variant: "destructive",
        });
        console.error("Failed to load blog post detail:", error);
      } finally {
        setLoading(false);
      }
    };

    void loadPost();
  }, [slug, initialPost, toast]);

  if (loading)
    return (
      <>
        <NavbarSection />
        <Container className="py-5 min-vh-100 d-flex flex-column align-items-center justify-content-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading post...</p>
        </Container>
        <FooterSection />
      </>
    );

  if (!post)
    return (
      <>
        <NavbarSection />
        <Container className="py-5 min-vh-100">
          <Alert variant="warning" className="p-4 rounded-3 shadow-sm">
            <Alert.Heading>Post Not Found</Alert.Heading>
            <p>The blog post you’re looking for doesn’t exist.</p>
            <Link href="/blog" className="btn btn-primary mt-3">
              <ArrowLeft size={16} className="me-2" />
              Back to Blog
            </Link>
          </Alert>
        </Container>
        <FooterSection />
      </>
    );

  return (
    <>
      <NavbarSection />
      <BlogDetailContent post={post} />
      <FooterSection />
    </>
  );
};

export default BlogDetailClient;
